import { useTranslation } from "react-i18next";
import { ChangeEventHandler, DragEvent, useRef, useState } from "react";
import { CircularProgress, Link } from "@material-ui/core";
import {
  DraggedFileHelper,
  DropAndDropAreaContainer,
  ImportIcon,
  SaveProgressText,
} from "./styled";
import { LoadedFile } from "../../types";
import useCsvAccountParser from "./useCsvParser";
import { ActionType } from "../../reducer";

export type ImportStatus = "VALID" | "INVALID" | "IDLE" | "LOADING";

interface Props {
  onImport: (files: LoadedFile[]) => void;
  action: ActionType;
}

export default function ImportFileSection({ onImport, action }: Props) {
  const { t } = useTranslation("accounts");

  const { validateFiles, parseFiles } = useCsvAccountParser(action);
  const [status, setStatus] = useState<ImportStatus>("IDLE");
  const [importError, setImportError] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { items } = e.dataTransfer;
    const csvFiles = Array.from(items).every(i => i.type === "text/csv");

    if (csvFiles) return setStatus("VALID");

    setImportError("importAccountsModal.import.fileTypeError");
    setStatus("INVALID");
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setStatus("IDLE");
  };

  async function validateAndParse(files: File[]) {
    const error = validateFiles(files);
    if (error) {
      setImportError(error);
      return setStatus("INVALID");
    }

    setStatus("LOADING");
    const loadedFiles = await parseFiles(files);
    onImport(loadedFiles);
    setStatus("IDLE");
  }

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (status === "INVALID") return setStatus("IDLE");

    const { items } = e.dataTransfer;
    const files = Array.from(items).map(i => i.getAsFile());

    await validateAndParse(files);
  };

  const onFileSelect: ChangeEventHandler<HTMLInputElement> = async e => {
    const files = Array.from(e.target.files);
    await validateAndParse(files);
    fileInputRef.current.value = "";
  };

  return (
    <DropAndDropAreaContainer
      status={status}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {status === "LOADING" && <CircularProgress />}
      {status !== "LOADING" && (
        <>
          <ImportIcon />
          <SaveProgressText variant="caption">
            {t("importAccountsModal.import.dragFiles")}
            <input
              type="file"
              id="csv-file"
              accept=".csv"
              multiple
              ref={fileInputRef}
              onChange={onFileSelect}
            />
            <label htmlFor="csv-file">
              <Link>{t("importAccountsModal.import.selectFileLink")}</Link>
            </label>
          </SaveProgressText>
          {status === "INVALID" && (
            <DraggedFileHelper error>{t(importError)}</DraggedFileHelper>
          )}
        </>
      )}
    </DropAndDropAreaContainer>
  );
}
